import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { SplitterPanelDirective } from './splitter-panel.directive';

export type SPLIT_PANEL_LAYOUT = 'horizontal' | 'vertical';

@Component({
  selector: 'app-splitter',
  templateUrl: './splitter.component.html',
  styleUrls: ['./splitter.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SplitterComponent implements AfterContentInit, AfterViewInit {
  @Input() styleClass: string;

  @Input() panelStyleClass: string;

  @Input() style: any;

  @Input() gutterIconStyle: any;

  @Input() panelStyle: any;

  @Input() layout: SPLIT_PANEL_LAYOUT = 'horizontal';

  @Input() gutterSize = 4;

  @Input() minSizes: number[] = [];

  @Input() splitterLabel = '';

  @Input() gutterAnimation = true;

  @Input() panelType = 1;

  @Input() enableHalfSplit = true;

  @Input() splitByPanelLength = true;

  @Input() gridSize = 1;

  @Input() get leftCollapsePanelSize(): number[] {
    return this._leftCollapsePanelSize;
  }

  set leftCollapsePanelSize(val: number[]) {
    this._leftCollapsePanelSize = val;
  }

  @Input() get rightCollapsePanelSize(): number[] {
    return this._rightCollapsePanelSize;
  }

  set rightCollapsePanelSize(val: number[]) {
    this._rightCollapsePanelSize = val;
  }

  @Input() get panelSizes(): number[] {
    return this._panelSizes;
  }
  set panelSizes(val: number[]) {
    this._panelSizes = val;
    this._initialPanelSizes = val;
  }

  @Input() get onLoadPanelSizes(): number[] {
    return this._onLoadPanelSizes;
  }
  set onLoadPanelSizes(val: number[]) {
    this._onLoadPanelSizes = val;
  }

  @ContentChildren(SplitterPanelDirective) templates: QueryList<SplitterPanelDirective>;

  @ViewChild('container', { static: false }) containerViewChild: ElementRef;

  @Output() panelSizeChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  panels = [];

  _initialPanelSizes: number[] = [];

  _onLoadPanelSizes: number[] = [];

  _panelSizes: number[] = [];

  _leftCollapsePanelSize: number[] = [];

  _rightCollapsePanelSize: number[] = [];

  gridSize$ = new BehaviorSubject<number>(1);

  gridCollapsed$ = this.gridSize$.pipe(map((size: number) => size === 0));

  gridFullWidth$ = this.gridSize$.pipe(map((size: number) => size === 2));

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly el: ElementRef
  ) {}

  ngAfterViewInit(): void {
    this.setPanelSizes();
  }

  ngAfterContentInit(): void {
    this.templates.forEach((item) => {
      this.panels.push(item.template);
    });
  }

  updatePanelSize(increment: number): void {
    let sizeToSet = this.gridSize$.value + increment;
    sizeToSet = Math.max(0, Math.min(2, sizeToSet));
    this.gridSize$.next(sizeToSet);
    this.setUpdatedPanelSize(sizeToSet);
  }

  setGridAndPanelSize(sizeToSet: number): void {
    this.gridSize$.next(sizeToSet);
    this.setUpdatedPanelSize(sizeToSet);
  }

  private setUpdatedPanelSize(sizeToSet: number) {
    if (sizeToSet === 0) {
      this._panelSizes = Object.assign([], this._leftCollapsePanelSize);
    } else if (sizeToSet === 2) {
      this._panelSizes = Object.assign([], this._rightCollapsePanelSize);
    } else {
      this._panelSizes = Object.assign([], this._initialPanelSizes);
    }
    const children = [...this.containerViewChild.nativeElement.children].filter((child: HTMLElement) =>
      child.classList.contains('cm-splitter-panel')
    );
    children.forEach((child: HTMLElement, i: number) => {
      child.style.flexBasis = 'calc(' + this._panelSizes[i] + '% - 0px)';
      if (this._panelSizes[i] === 0) {
        child.style.visibility = 'hidden';
        child.style.overflow = 'hidden';
      } else {
        child.style.visibility = 'visible';
        child.style.overflow = 'auto';
      }
    });
    this.panelSizeChange.emit();
    this.cd.detectChanges();
  }

  private setPanelSizes(): void {
    if (this.panels?.length > 0) {
      const children = [...this.el.nativeElement.children[0].children].filter((child: HTMLElement) =>
        child.classList.contains('cm-splitter-panel')
      );
      const _panelSizes = [];

      this.panels.forEach((panel, i) => {
        let panelInitialSize = this.onLoadPanelSizes.length - 1 >= i ? this.onLoadPanelSizes[i] : null;
        if (panelInitialSize === null || panelInitialSize === undefined) {
          panelInitialSize = 100;
        }
        const panelSize = this.splitByPanelLength ? panelInitialSize / this.panels.length : panelInitialSize;
        _panelSizes[i] = panelSize;
        if (children?.length > 0) {
          children[i].style.flexBasis =
            'calc(' + panelSize + '% - ' + (this.panels.length - 1) * this.gutterSize + 'px)';
        }
      });

      this._panelSizes = _panelSizes;
      this.gridSize$.next(this.gridSize);
    }
  }
}
